// File: /js/shared/components/headerAutohide.js
//
// Phase 2 of the module-view header rework. On the content / module reading
// surfaces, let the sticky site header get OUT OF THE WAY while the reader
// scrolls down — reclaiming the full mobile viewport (the band measures
// ~95px logged-in / ~71px logged-out at 320px after the Phase-1 trim) — and
// slide it back on scroll-up (the standard mobile auto-hide pattern). The
// "title behind the header" symptom dissolves: there is no persistent band to
// sit behind once you start reading.
//
// Deliberately narrow scope:
//   - MOBILE ONLY (<=1049px — the regime where the menu collapses to a
//     hamburger and the band is tall). Desktop keeps its short, always-pinned
//     band, so global sticky behaviour there is untouched.
//   - The content / module reading pages only (`body[data-page]` allowlist).
//     Every other page keeps the header permanently pinned.
//   - Never hides while an overlay owns the header: the slide-in menu
//     (`#menu-toggle.open`, whose X-to-close lives in the band) or a modal
//     (`body.modal-open`).
//
// Element-scoped by design: a single PASSIVE `window` scroll listener that
// toggles one class on `.sticky-header`. No document-level AJAX seam, no new
// DOM contract attribute (not a §1.3 rule-9 seam).

const HIDE_CLASS = 'sticky-header--hidden';
const READING_PAGES = new Set(['content', 'module']);
const MOBILE_QUERY = '(max-width: 1049px)';
const DOWN_THRESHOLD = 8; // px of downward travel before hiding (ignore jitter)
const TOP_REVEAL_ZONE = 64; // always show within this many px of the top

export function initHeaderAutohide() {
  const header = document.querySelector('.sticky-header');
  if (!header) return;

  const mq = window.matchMedia(MOBILE_QUERY);
  let lastY = window.scrollY || 0;
  let ticking = false;

  const show = () => header.classList.remove(HIDE_CLASS);
  const hide = () => header.classList.add(HIDE_CLASS);

  // Active = mobile regime AND a reading page. Read live so an AJAX content
  // transition that swaps `body[data-page]` is honoured without rebinding.
  const isActive = () =>
    mq.matches && READING_PAGES.has(document.body.dataset.page || '');

  // An open menu/modal owns the header — keep it visible regardless.
  const overlayOpen = () =>
    document.body.classList.contains('modal-open') ||
    !!document.querySelector('#menu-toggle.open');

  function update() {
    ticking = false;
    const y = window.scrollY || 0;

    if (!isActive() || overlayOpen()) {
      show();
      lastY = y;
      return;
    }

    if (y <= TOP_REVEAL_ZONE) {
      show();
    } else if (y > lastY + DOWN_THRESHOLD) {
      hide(); // scrolling down — get out of the way
    } else if (y < lastY) {
      show(); // scrolling up — bring it back
    }
    lastY = y;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    },
    { passive: true }
  );

  // Leaving the mobile regime (rotate / resize to desktop) must never strand
  // the header hidden.
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', show);
  }
}
